export function getAndroidVersionName(sdk: number): string {
  const sdkMap: Record<number, string> = {
    24: 'Android 7.0 (Nougat)',
    25: 'Android 7.1 (Nougat)',
    26: 'Android 8.0 (Oreo)',
    27: 'Android 8.1 (Oreo)',
    28: 'Android 9.0 (Pie)',
    29: 'Android 10.0',
    30: 'Android 11.0',
    31: 'Android 12.0',
    32: 'Android 12.1',
    33: 'Android 13.0',
    34: 'Android 14.0',
    35: 'Android 15.0',
  };
  return sdkMap[sdk] || `API Level ${sdk}`;
}

type PermissionCategory = 'normal' | 'dangerous' | 'special';

function categorizePermission(permission: string): PermissionCategory {
  const normalPerms = [
    'INTERNET',
    'ACCESS_NETWORK_STATE',
    'ACCESS_WIFI_STATE',
    'VIBRATE',
    'WAKE_LOCK',
    'RECEIVE_BOOT_COMPLETED',
    'FOREGROUND_SERVICE',
  ];
  const dangerousPerms = [
    'CAMERA',
    'ACCESS_FINE_LOCATION',
    'ACCESS_COARSE_LOCATION',
    'READ_PHONE_STATE',
    'RECORD_AUDIO',
    'READ_EXTERNAL_STORAGE',
    'WRITE_EXTERNAL_STORAGE',
    'USE_BIOMETRIC',
    'USE_FINGERPRINT',
    'POST_NOTIFICATIONS',
  ];
  const specialPerms = [
    'SYSTEM_ALERT_WINDOW',
    'WRITE_SETTINGS',
    'INSTALL_PACKAGES',
    'DELETE_PACKAGES',
  ];

  const permName = permission.replace('android.permission.', '');
  if (normalPerms.includes(permName)) return 'normal';
  if (dangerousPerms.includes(permName)) return 'dangerous';
  if (specialPerms.includes(permName)) return 'special';
  return 'normal';
}

export function formatPermissionName(permission: string): string {
  return permission.replace('android.permission.', '').replace(/_/g, ' ');
}

interface CategorizedPermissions {
  normal: string[];
  dangerous: string[];
  special: string[];
}

export function getCategorizedPermissions(
  permissions: string[] | undefined,
): CategorizedPermissions {
  if (!permissions) return { normal: [], dangerous: [], special: [] };

  const perms: CategorizedPermissions = {
    normal: [],
    dangerous: [],
    special: [],
  };

  for (const perm of permissions) {
    const category = categorizePermission(perm);
    perms[category].push(perm);
  }

  return perms;
}

const DANGEROUS_PERMISSIONS = [
  'INSTALL_PACKAGES',
  'DELETE_PACKAGES',
  'SYSTEM_ALERT_WINDOW',
  'WRITE_SECURE_SETTINGS',
] as const;

export function isDangerousPermission(permission: string): boolean {
  return DANGEROUS_PERMISSIONS.some((dangerous) =>
    permission.includes(dangerous),
  );
}

export function getDangerousPermissions(permissions: string[]): string[] {
  return permissions.filter(isDangerousPermission);
}

export function hasDangerousPermissions(permissions: string[]): boolean {
  return permissions.some(isDangerousPermission);
}
